import React, { useState, useEffect } from 'react'
import { Layout, Card, Row, Col, Button, Typography, theme, Space, Select, notification, Modal, Avatar } from 'antd'
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined
} from '@ant-design/icons'
import { getDoctorAvailability } from '../../api/searchApi'
import AppointmentConfirmationMobile from './AppointmentConfirmationMobile'

const { Header, Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

const DoctorAvailabilityViewMobile = ({
  doctor,
  onClose,
  onBookAppointment,
  isVirtualVisit = false,
  searchFilters = {},
  preSelectedSlot = null
}) => {
  const { token } = theme.useToken()
  const [loading, setLoading] = useState(false)
  const [selectedReason, setSelectedReason] = useState(searchFilters.appointmentReason || null)
  const [selectedServiceType, setSelectedServiceType] = useState(
    searchFilters.serviceType || (isVirtualVisit ? null : 'scheduled')
  )
  const [selectedCallType, setSelectedCallType] = useState(searchFilters.callType || (isVirtualVisit ? 'video' : null))
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [dischemData, setDischemData] = useState({
    required: false,
    reqType: 'C', // Collection or Delivery
    store: null
  })
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date())
  const [availabilityData, setAvailabilityData] = useState(null)
  const [currentView, setCurrentView] = useState('calendar') // 'calendar' or 'confirmation'
  const [timeSlots, setTimeSlots] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [showSlotModal, setShowSlotModal] = useState(false)
  const [pendingSlot, setPendingSlot] = useState(null)
  const [flexProceed, setFlexProceed] = useState(false)

  // Check if this is a flexi/priority consultation
  const isFlexiOrPriority = isVirtualVisit && (selectedServiceType === 'flexi' || selectedServiceType === 'priority')

  // Handle pre-selected slot from quick booking
  useEffect(() => {
    if (preSelectedSlot) {
      console.log('🎯 Mobile - Pre-selected slot provided:', preSelectedSlot)
      setSelectedSlot(preSelectedSlot)
      setCurrentView('confirmation') // Skip calendar view and go directly to confirmation
    }
  }, [preSelectedSlot])

  // For flexi/priority, go directly to confirmation when reason is selected
  const handleFlexiPriorityProceed = () => {
    if (!selectedReason) {
      notification.warning({
        message: 'Missing Information',
        description: 'Please select an appointment reason to continue.',
        duration: 3
      })
      return
    }
    // Create a virtual slot for flexi/priority
    setSelectedSlot({
      time: null,
      date: new Date(),
      isFlexiPriority: true
    })
    setCurrentView('confirmation')
  }

  useEffect(() => {
    fetchAvailability()
  }, [doctor, currentWeekStart])

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const fetchAvailability = async () => {
    if (!doctor) return

    setLoading(true)
    try {
      const filter = {
        medicalProfessional: doctor._id,
        clinicRef: doctor.clinicRecord?._id
      }

      const availability = await getDoctorAvailability(filter)
      setAvailabilityData(availability)
      setTimeSlots(generateTimeSlots(availability))
    } catch (error) {
      console.error('Error fetching availability:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMonday = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  }

  const getWeekDates = (startDate) => {
    const dates = []
    const monday = getMonday(startDate)

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const formatDate = (date) => {
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
    const day = dayNames[date.getDay()]
    const dayNum = date.getDate()
    const month = date.getMonth() + 1
    return { day, dayNum, month, fullDate: `${day} ${dayNum}/${month}` }
  }

  const generateTimeSlots = (availability) => {
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
    const weekDates = getWeekDates(currentWeekStart)

    // All possible time slots
    const allPossibleTimes = [
      '06:00',
      '06:15',
      '06:30',
      '06:45',
      '07:00',
      '07:15',
      '07:30',
      '07:45',
      '08:00',
      '08:15',
      '08:30',
      '08:45',
      '09:00',
      '09:15',
      '09:30',
      '09:45',
      '10:00',
      '10:15',
      '10:30',
      '10:45',
      '11:00',
      '11:15',
      '11:30',
      '11:45',
      '12:00',
      '12:15',
      '12:30',
      '12:45',
      '13:00',
      '13:15',
      '13:30',
      '13:45',
      '14:00',
      '14:15',
      '14:30',
      '14:45',
      '15:00',
      '15:15',
      '15:30',
      '15:45',
      '16:00',
      '16:15',
      '16:30',
      '16:45',
      '17:00',
      '17:15',
      '17:30',
      '17:45'
    ]

    // Build the grid and filter out time rows with no available slots
    const timeRowsWithAvailability = allPossibleTimes
      .map((time) => {
        const slots = weekDates.map((date, dayIndex) => {
          const dateStr = date.toISOString().split('T')[0]

          if (dateStr < todayStr) {
            return {
              date,
              dayIndex,
              time,
              available: false,
              id: `${dayIndex}-${time}`,
              slotId: null
            }
          }

          const dayAvailability = availability?.availability?.find((avail) => avail.date === dateStr)
          const timeSlot = dayAvailability?.timeSlots?.find((slot) => slot.time === time)

          return {
            date,
            dayIndex,
            time,
            available: timeSlot ? !timeSlot.blocked : false,
            id: `${dayIndex}-${time}`,
            slotId: timeSlot?._id
          }
        })

        // Check if this time row has at least one available slot across all days
        const hasAvailableSlot = slots.some((slot) => slot.available)

        return {
          time,
          slots,
          hasAvailableSlot
        }
      })
      .filter((timeRow) => timeRow.hasAvailableSlot) // Only keep time rows with at least one available slot

    return timeRowsWithAvailability
  }

  const handleSlotClick = (slot) => {
    if (!selectedReason) {
      notification.warning({
        message: 'Select Appointment Reason',
        description: 'Please select a reason for your visit before choosing a time slot.',
        duration: 3
      })
      return
    }

    // Set the pending slot and show modal
    setPendingSlot(slot)
    setShowSlotModal(true)
  }

  const handleConfirmSlot = () => {
    // Set the selected slot to show visual feedback
    setSelectedSlot(pendingSlot)
    setShowSlotModal(false)

    // Add a small delay before transitioning to confirmation for better UX
    setTimeout(() => {
      setCurrentView('confirmation')
      // Scroll to top when switching to confirmation view
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 200)
  }

  const handleCancelSlot = () => {
    setPendingSlot(null)
    setShowSlotModal(false)
  }

  const navigateWeek = (direction) => {
    const today = new Date()
    const currentWeekMonday = getMonday(today)
    const newDate = new Date(currentWeekStart)
    newDate.setDate(newDate.getDate() + direction * 7)
    const newWeekMonday = getMonday(newDate)

    console.log('🗓️ Mobile Week Navigation Debug:', {
      direction,
      currentWeekStart: currentWeekStart.toISOString().split('T')[0],
      newWeekMonday: newWeekMonday.toISOString().split('T')[0],
      currentWeekMonday: currentWeekMonday.toISOString().split('T')[0],
      canNavigate: newWeekMonday >= currentWeekMonday
    })

    // Prevent going back to past weeks (before this week)
    if (direction < 0) {
      if (newWeekMonday < currentWeekMonday) {
        console.log('🚫 Mobile: Blocked navigation to past week')
        return
      }
    }

    console.log('✅ Mobile: Navigating to week:', newWeekMonday.toISOString().split('T')[0])
    setCurrentWeekStart(newWeekMonday)
  }

  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Medical Certificate',
    'Prescription Renewal',
    'Mental Health Consultation',
    'Assessment',
    'Other'
  ]

  // Service types for virtual visits (flexi/priority vs scheduled)
  const serviceTypes = [
    {
      value: 'scheduled',
      label: 'Scheduled Consultation',
      description: 'Book a specific time slot with your selected doctor',
      icon: 'calendar'
    },
    {
      value: 'flexi',
      label: 'Flexi Consultation',
      description: 'Doctor will contact you during the course of the day',
      icon: 'clock'
    },
    {
      value: 'priority',
      label: 'Priority Consultation',
      description: 'Doctor will call within a few minutes',
      icon: 'urgent'
    }
  ]

  // Call types for virtual consultations
  const callTypes = [
    {
      value: 'video',
      label: 'In App Video',
      icon: 'video'
    },
    {
      value: 'whatsapp',
      label: 'WhatsApp Video',
      icon: 'whatsapp'
    },
    {
      value: 'phone',
      label: 'Phone Call',
      icon: 'phone'
    }
  ]

  const handleConfirmBooking = async (bookingData) => {
    try {
      await onBookAppointment?.(bookingData)
    } catch (error) {
      console.error('Booking error:', error)
    }
  }

  // Get time slots for a specific day
  const getTimeSlotsForDay = (dayIndex) => {
    const slots = []
    timeSlots.forEach((timeSlot) => {
      const slot = timeSlot.slots[dayIndex]
      if (slot) {
        slots.push(slot)
      }
    })
    return slots
  }

  // Don't auto-select day - let user choose manually
  // useEffect(() => {
  //   if (timeSlots.length > 0 && selectedDay === null) {
  //     const today = new Date()
  //     const weekDates = getWeekDates(currentWeekStart)

  //     // Try to find today in the current week
  //     const todayIndex = weekDates.findIndex((date) => date.toDateString() === today.toDateString())

  //     if (todayIndex !== -1 && todayIndex < weekDates.length) {
  //       // Check if today has available slots
  //       const todaySlots = getTimeSlotsForDay(todayIndex)
  //       if (todaySlots.some((slot) => slot.available)) {
  //         setSelectedDay(todayIndex)
  //         return
  //       }
  //     }

  //     // Find first day with available slots
  //     for (let i = 0; i < weekDates.length; i++) {
  //       const daySlots = getTimeSlotsForDay(i)
  //       if (daySlots.some((slot) => slot.available)) {
  //         setSelectedDay(i)
  //         break
  //       }
  //     }
  //   }
  // }, [timeSlots, currentWeekStart])

  // Reset selected day when week changes
  useEffect(() => {
    setSelectedDay(null)
  }, [currentWeekStart])

  if (
    currentView === 'confirmation' &&
    (selectedSlot ||
      (isVirtualVisit && (selectedServiceType === 'flexi' || selectedServiceType === 'priority') && flexProceed))
  ) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <Button
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              console.log('Here at confirm')
              setCurrentView('calendar')
              setFlexProceed(false)
              // Scroll to top when going back to calendar
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            style={{ marginRight: '12px' }}
          />
          <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
            Confirm Booking
          </Title>
        </Header>

        <Content style={{ padding: 0 }}>
          <div className='mobile-availability-container'>
            <AppointmentConfirmationMobile
              doctor={doctor}
              selectedSlot={selectedSlot}
              selectedReason={selectedReason}
              selectedServiceType={selectedServiceType}
              selectedCallType={selectedCallType}
              dischemData={dischemData}
              onConfirm={handleConfirmBooking}
              onBack={() => {
                setCurrentView('calendar')
                setFlexProceed(false)
                // Scroll to top when going back to calendar
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              isVirtualVisit={isVirtualVisit}
              searchFilters={searchFilters}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // Show loading animation
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <Button type='text' icon={<ArrowLeftOutlined />} onClick={onClose} style={{ marginRight: '12px' }} />
          <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
            Loading Availability...
          </Title>
        </Header>
        <Content
          style={{
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '16px',
              margin: '16px'
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px'
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  border: '3px solid rgba(59, 130, 246, 0.2)',
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  position: 'absolute'
                }}
              />
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s infinite'
                }}
              >
                <CalendarOutlined style={{ fontSize: '18px', color: 'white' }} />
              </div>
            </div>
            <Title
              level={4}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                margin: '0 0 8px 0',
                fontSize: '18px',
                textAlign: 'center'
              }}
            >
              Loading Availability
            </Title>
            <Text style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
              Checking {doctor?.firstName}'s schedule...
            </Text>
          </div>
        </Content>
      </Layout>
    )
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Button type='text' icon={<ArrowLeftOutlined />} onClick={onClose} style={{ marginRight: '12px' }} />
        <Title
          level={5}
          style={{
            margin: 0,
            fontSize: '16px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          Book with {doctor?.firstName} {doctor?.lastName}
        </Title>
      </Header>

      <Content style={{ padding: '16px', overflow: 'auto' }} className='mobile-availability-container'>
        {/* Doctor Info Card */}
        <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
          <Row align='middle' gutter={16}>
            <Col>
              <Avatar size={64} src={doctor.image} icon={<UserOutlined />} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
                {doctor?.firstName} {doctor?.lastName}
              </Title>
              <Text type='secondary' style={{ fontSize: '14px' }}>
                {doctor.extraData?.profession}
              </Text>
              <br />
              <Text style={{ fontSize: '12px', color: token.colorPrimary }}>{doctor.clinicRecord?.name}</Text>
            </Col>
          </Row>
        </Card>

        {/* Appointment Reason - Only show if not already captured in search criteria */}
        {!searchFilters.appointmentReason && (
          <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
            <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
              <CalendarOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Select Appointment Reason
            </Title>
            <Select
              placeholder='Choose the reason for your visit...'
              value={selectedReason}
              onChange={setSelectedReason}
              style={{ width: '100%' }}
              size='large'
            >
              {appointmentReasons.map((reason) => (
                <Option key={reason} value={reason}>
                  {reason}
                </Option>
              ))}
            </Select>
          </Card>
        )}

        {/* Service Type Selection - Only for Virtual Visits and only if not already selected */}
        {isVirtualVisit && !searchFilters.serviceType && (
          <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
            <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Select Service Type
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {serviceTypes.map((serviceType) => (
                <div
                  key={serviceType.value}
                  onClick={() => setSelectedServiceType(serviceType.value)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${selectedServiceType === serviceType.value ? token.colorPrimary : '#d9d9d9'}`,
                    borderRadius: '8px',
                    backgroundColor: selectedServiceType === serviceType.value ? '#f0f9ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${
                          selectedServiceType === serviceType.value ? token.colorPrimary : '#d9d9d9'
                        }`,
                        backgroundColor: selectedServiceType === serviceType.value ? token.colorPrimary : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '2px'
                      }}
                    >
                      {selectedServiceType === serviceType.value && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          fontSize: '16px',
                          color: selectedServiceType === serviceType.value ? token.colorPrimary : '#000'
                        }}
                      >
                        {serviceType.label}
                      </Text>
                      <div style={{ marginTop: '4px' }}>
                        <Text style={{ fontSize: '14px', color: '#666' }}>{serviceType.description}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Call Type Selection - Only for Virtual Visits and only if not already selected */}
        {isVirtualVisit && selectedServiceType && !searchFilters.callType && (
          <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
            <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
              <CalendarOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Select Contact Method
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {callTypes.map((callType) => (
                <div
                  key={callType.value}
                  onClick={() => setSelectedCallType(callType.value)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${selectedCallType === callType.value ? token.colorPrimary : '#d9d9d9'}`,
                    borderRadius: '8px',
                    backgroundColor: selectedCallType === callType.value ? '#f0f9ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${selectedCallType === callType.value ? token.colorPrimary : '#d9d9d9'}`,
                        backgroundColor: selectedCallType === callType.value ? token.colorPrimary : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {selectedCallType === callType.value && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                      )}
                    </div>
                    <Text
                      strong
                      style={{
                        fontSize: '16px',
                        color: selectedCallType === callType.value ? token.colorPrimary : '#000'
                      }}
                    >
                      {callType.label}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Dis-Chem Medication Delivery - Only for Virtual Visits */}
        {isVirtualVisit && selectedServiceType && selectedCallType && (
          <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
            <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
              <EnvironmentOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Prescription Service (Optional)
            </Title>
            <div style={{ marginBottom: '16px' }}>
              <Text style={{ fontSize: '14px', color: '#666' }}>
                Complete this section if you'd like your prescription delivered or ready for collection at Dis-Chem.
              </Text>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                onClick={() => setDischemData({ ...dischemData, required: true })}
                style={{
                  padding: '16px',
                  border: `2px solid ${dischemData.required === true ? token.colorPrimary : '#d9d9d9'}`,
                  borderRadius: '8px',
                  backgroundColor: dischemData.required === true ? '#f0f9ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${dischemData.required === true ? token.colorPrimary : '#d9d9d9'}`,
                      backgroundColor: dischemData.required === true ? token.colorPrimary : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {dischemData.required === true && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                    )}
                  </div>
                  <Text
                    strong
                    style={{ fontSize: '16px', color: dischemData.required === true ? token.colorPrimary : '#000' }}
                  >
                    Yes, I want Dis-Chem service
                  </Text>
                </div>
              </div>

              <div
                onClick={() => setDischemData({ ...dischemData, required: false, reqType: null, store: null })}
                style={{
                  padding: '16px',
                  border: `2px solid ${dischemData.required === false ? token.colorPrimary : '#d9d9d9'}`,
                  borderRadius: '8px',
                  backgroundColor: dischemData.required === false ? '#f0f9ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${dischemData.required === false ? token.colorPrimary : '#d9d9d9'}`,
                      backgroundColor: dischemData.required === false ? token.colorPrimary : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {dischemData.required === false && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                    )}
                  </div>
                  <Text
                    strong
                    style={{ fontSize: '16px', color: dischemData.required === false ? token.colorPrimary : '#000' }}
                  >
                    No, I'll handle my prescription myself
                  </Text>
                </div>
              </div>
            </div>

            {/* Service Type Options */}
            {dischemData.required && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <Text strong style={{ fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                  Service Type:
                </Text>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    type={dischemData.reqType === 'C' ? 'primary' : 'default'}
                    onClick={() => setDischemData({ ...dischemData, reqType: 'C' })}
                    style={{ flex: 1 }}
                  >
                    Collection
                  </Button>
                  <Button
                    type={dischemData.reqType === 'D' ? 'primary' : 'default'}
                    onClick={() => setDischemData({ ...dischemData, reqType: 'D' })}
                    style={{ flex: 1 }}
                  >
                    Delivery
                  </Button>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    Store selection will be available during the consultation process.
                  </Text>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Calendar - Only show when appointment reason is selected and it's a scheduled consultation */}
        {selectedReason && (!isVirtualVisit || selectedServiceType === 'scheduled') && (
          <Card style={{ borderRadius: '12px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
            >
              <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
                <ClockCircleOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
                Available Times
              </Title>
              <Space>
                <Button
                  type='text'
                  onClick={() => navigateWeek(-1)}
                  disabled={getMonday(currentWeekStart) <= getMonday(new Date())}
                  style={{ fontSize: '18px', height: '32px', width: '32px' }}
                >
                  ←
                </Button>
                <Button
                  type='text'
                  onClick={() => navigateWeek(1)}
                  style={{ fontSize: '18px', height: '32px', width: '32px' }}
                >
                  →
                </Button>
              </Space>
            </div>

            {/* Selectable Days */}
            <div style={{ marginBottom: '16px' }}>
              <Row gutter={[8, 8]}>
                {getWeekDates(currentWeekStart).map((date, index) => {
                  const formatted = formatDate(date)
                  const today = new Date()
                  const isToday = date.toDateString() === today.toDateString()
                  const isSelected = selectedDay === index
                  const isPast = date < new Date(new Date().setHours(0, 0, 0, 0))

                  return (
                    <Col key={index} span={8} style={{ marginBottom: '8px' }}>
                      <Button
                        block
                        type={isSelected ? 'primary' : 'default'}
                        disabled={isPast}
                        onClick={() => setSelectedDay(index)}
                        style={{
                          height: '56px',
                          borderRadius: '8px',
                          backgroundColor: isSelected
                            ? token.colorPrimary
                            : isToday && !isSelected
                            ? '#e6f7ff'
                            : isPast
                            ? '#f5f5f5'
                            : '#fff',
                          borderColor: isSelected
                            ? token.colorPrimary
                            : isToday && !isSelected
                            ? token.colorPrimary
                            : '#d9d9d9',
                          color: isSelected ? '#fff' : isPast ? '#bbb' : '#000',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          fontSize: '12px'
                        }}
                      >
                        <div>{formatted.day}</div>
                        <div style={{ fontSize: '14px', marginTop: '2px' }}>
                          {formatted.dayNum}/{formatted.month}
                        </div>
                      </Button>
                    </Col>
                  )
                })}
              </Row>
            </div>

            {/* Time Slots for Selected Day - Only show when day is selected */}
            {selectedDay !== null ? (
              <div>
                <Title level={5} style={{ fontSize: '14px', marginBottom: '12px', color: '#666' }}>
                  Available Times - {formatDate(getWeekDates(currentWeekStart)[selectedDay]).fullDate}
                </Title>

                <Row gutter={[8, 8]}>
                  {getTimeSlotsForDay(selectedDay)
                    .filter((slot) => slot.available) // Only show available slots
                    .map((slot) => {
                      const isSelected = selectedSlot?.id === slot.id
                      const isPending = pendingSlot?.id === slot.id
                      const canSelect = slot.available && selectedReason

                      return (
                        <Col key={slot.id} span={12} style={{ marginBottom: '8px' }}>
                          <Button
                            block
                            type={isSelected || isPending ? 'primary' : 'default'}
                            disabled={!canSelect}
                            onClick={() => canSelect && handleSlotClick(slot)}
                            className={`mobile-time-slot-button ${isSelected || isPending ? 'selected' : ''}`}
                            style={{
                              height: '48px',
                              fontSize: '16px',
                              fontWeight: '600',
                              borderRadius: '8px',
                              backgroundColor: isSelected || isPending ? token.colorPrimary : '#fff',
                              borderColor: isSelected || isPending ? token.colorPrimary : '#d9d9d9',
                              color: isSelected || isPending ? '#fff' : '#000'
                            }}
                          >
                            {slot.time}
                          </Button>
                        </Col>
                      )
                    })}
                  {getTimeSlotsForDay(selectedDay).filter((slot) => slot.available).length === 0 && (
                    <Col span={24}>
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          background: '#f9fafb',
                          borderRadius: '8px',
                          border: '1px dashed #d1d5db'
                        }}
                      >
                        <CalendarOutlined style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '12px' }} />
                        <Title level={5} style={{ color: '#6b7280', margin: '0 0 8px 0' }}>
                          No Available Slots
                        </Title>
                        <Text style={{ color: '#9ca3af' }}>
                          No appointments available for this day. Try selecting another day.
                        </Text>
                      </div>
                    </Col>
                  )}
                </Row>

                {getTimeSlotsForDay(selectedDay).length === 0 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '24px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      border: '1px solid #e5e5e5'
                    }}
                  >
                    <Text style={{ color: '#666', fontSize: '14px' }}>No available time slots for this day</Text>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#e6f7ff',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  marginTop: '16px'
                }}
              >
                <ClockCircleOutlined style={{ fontSize: '48px', color: token.colorPrimary, marginBottom: '16px' }} />
                <Title level={4} style={{ color: token.colorPrimary, margin: '0 0 8px 0' }}>
                  Select a Date
                </Title>
                <Text style={{ color: '#1890ff', fontSize: '14px' }}>
                  Choose a date above to view available appointment times
                </Text>
              </div>
            )}
          </Card>
        )}

        {/* Show message when appointment reason is NOT selected or it's not a scheduled consultation */}
        {(!selectedReason || (isVirtualVisit && selectedServiceType !== 'scheduled')) && (
          <Card style={{ borderRadius: '12px' }}>
            {/* Show different messages based on what's missing */}
            {!selectedReason ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#fef3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7'
                }}
              >
                <CalendarOutlined style={{ fontSize: '48px', color: '#d69e2e', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#744210', margin: '0 0 8px 0' }}>
                  Select Appointment Reason First
                </Title>
                <Text style={{ color: '#856404', fontSize: '14px' }}>
                  Please choose the reason for your visit above to continue
                </Text>
              </div>
            ) : isVirtualVisit && !selectedServiceType ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#fef3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7'
                }}
              >
                <ClockCircleOutlined style={{ fontSize: '48px', color: '#d69e2e', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#744210', margin: '0 0 8px 0' }}>
                  Select Service Type
                </Title>
                <Text style={{ color: '#856404', fontSize: '14px' }}>
                  Please choose your preferred consultation type above
                </Text>
              </div>
            ) : isVirtualVisit && (selectedServiceType === 'flexi' || selectedServiceType === 'priority') ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}
              >
                <CalendarOutlined style={{ fontSize: '48px', color: token.colorPrimary, marginBottom: '16px' }} />
                <Title level={4} style={{ color: token.colorPrimary, margin: '0 0 8px 0' }}>
                  {selectedServiceType === 'priority' ? 'Priority Consultation Ready' : 'Flexi Consultation Ready'}
                </Title>
                <Text style={{ color: '#1890ff', fontSize: '14px', marginBottom: '16px', display: 'block' }}>
                  {selectedServiceType === 'priority'
                    ? 'A doctor will call you within a few minutes after booking confirmation'
                    : 'A doctor will contact you during the course of the day'}
                </Text>
                {selectedCallType && (
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => {
                      // Handle flexi/priority booking confirmation
                      setFlexProceed(true)
                      setCurrentView('confirmation')
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                      border: 'none',
                      height: '48px',
                      minWidth: '200px'
                    }}
                  >
                    Proceed to Booking
                  </Button>
                )}
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#fef3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7'
                }}
              >
                <CalendarOutlined style={{ fontSize: '48px', color: '#d69e2e', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#744210', margin: '0 0 8px 0' }}>
                  Complete Selection
                </Title>
                <Text style={{ color: '#856404', fontSize: '14px' }}>
                  Please complete all selections above to continue
                </Text>
              </div>
            )}
          </Card>
        )}

        {/* Flexi/Priority Consultation UI - No calendar needed */}
        {selectedReason && isFlexiOrPriority && (
          <Card style={{ borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                  {selectedServiceType === 'priority' ? '🚨' : '🕐'}
                </div>
                <Title level={4} style={{ color: '#1f2937', marginBottom: '8px' }}>
                  {selectedServiceType === 'priority' ? 'Priority Consultation' : 'Flexi Consultation'}
                </Title>
                <Text style={{ color: '#6b7280', fontSize: '14px', display: 'block', marginBottom: '16px' }}>
                  {selectedServiceType === 'priority'
                    ? 'A doctor will call you within a few minutes'
                    : 'A doctor will contact you during the day'}
                </Text>
              </div>

              <Button
                type='primary'
                size='large'
                block
                onClick={handleFlexiPriorityProceed}
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
                }}
              >
                Continue to Payment
              </Button>
            </div>
          </Card>
        )}

        {/* Slot Confirmation Modal */}
        <Modal
          open={showSlotModal}
          onCancel={handleCancelSlot}
          onOk={handleConfirmSlot}
          centered
          title={null}
          footer={null}
          width={320}
          closable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CalendarOutlined
              style={{
                fontSize: '48px',
                color: token.colorPrimary,
                marginBottom: '16px',
                display: 'block'
              }}
            />
            <Title level={4} style={{ marginBottom: '8px' }}>
              Confirm Time Slot
            </Title>
            <Text style={{ fontSize: '16px', display: 'block', marginBottom: '16px' }}>
              Are you sure you want to book this appointment?
            </Text>
            {pendingSlot && (
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>Date: </Text>
                  <Text>{formatDate(pendingSlot.date).fullDate}</Text>
                </div>
                <div>
                  <Text strong>Time: </Text>
                  <Text>{pendingSlot.time}</Text>
                </div>
              </div>
            )}
            <Space size={12} style={{ width: '100%', justifyContent: 'center' }}>
              <Button size='large' onClick={handleCancelSlot} style={{ minWidth: '100px' }}>
                Cancel
              </Button>
              <Button
                type='primary'
                size='large'
                onClick={handleConfirmSlot}
                style={{
                  minWidth: '100px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  border: 'none'
                }}
              >
                Confirm
              </Button>
            </Space>
          </div>
        </Modal>
      </Content>
    </Layout>
  )
}

export default DoctorAvailabilityViewMobile
